import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { tradesApi } from "../lib/api";
import { fromLocalDatetimeInput, toLocalDatetimeInput } from "../lib/format";
import Select from "./Select";
import { useI18n } from "../lib/i18n";

export default function AddTradeForm({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const [side, setSide] = useState<"Buy" | "Sell">("Buy");
  const [status, setStatus] = useState<"closed" | "open">("closed");
  const [symbol, setSymbol] = useState("");
  const [assetClass, setAssetClass] = useState("Forex");
  const [profit, setProfit] = useState("");
  const [commission, setCommission] = useState("");
  const [swap, setSwap] = useState("");
  const [volume, setVolume] = useState("");
  const [openTime, setOpenTime] = useState(toLocalDatetimeInput(new Date().toISOString()));
  const [closeTime, setCloseTime] = useState(toLocalDatetimeInput(new Date().toISOString()));
  const [openPrice, setOpenPrice] = useState("");
  const [closePrice, setClosePrice] = useState("");
  const [sl, setSl] = useState("");
  const [tp, setTp] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: () =>
      tradesApi.create({
        side,
        status,
        symbol: symbol.trim() || null,
        asset_class: assetClass,
        profit: parseFloat(profit) || 0,
        commission: commission ? parseFloat(commission) : 0,
        swap: swap ? parseFloat(swap) : 0,
        volume: volume ? parseFloat(volume) : null,
        open_time: openTime ? fromLocalDatetimeInput(openTime) : null,
        close_time: status === "closed" && closeTime ? fromLocalDatetimeInput(closeTime) : null,
        open_price: openPrice ? parseFloat(openPrice) : null,
        close_price: closePrice ? parseFloat(closePrice) : null,
        sl: sl ? parseFloat(sl) : null,
        tp: tp ? parseFloat(tp) : null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => onSaved(),
    onError: (e) => setError((e as Error).message),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (status === "closed" && !closeTime) {
      setError(t("addTrade.closeTimeRequired"));
      return;
    }
    m.mutate();
  }

  return (
    <section className="glass p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-parchment">{t("addTrade.title")}</h2>
        <button type="button" onClick={onClose} className="text-parchment-muted text-sm hover:text-parchment">
          {t("common.cancel")}
        </button>
      </div>

      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setSide("Buy")}
            className={
              "rounded-xl border-2 px-3 py-2.5 text-sm font-bold transition " +
              (side === "Buy"
                ? "border-emerald-400/60 bg-emerald-500/15 text-pnl-pos"
                : "border-glass-mid bg-surface-1 text-parchment-muted hover:bg-surface-2")
            }
          >
            {t("addTrade.buy")}
          </button>
          <button
            type="button"
            onClick={() => setSide("Sell")}
            className={
              "rounded-xl border-2 px-3 py-2.5 text-sm font-bold transition " +
              (side === "Sell"
                ? "border-rose-400/60 bg-rose-500/15 text-pnl-neg"
                : "border-glass-mid bg-surface-1 text-parchment-muted hover:bg-surface-2")
            }
          >
            {t("addTrade.sell")}
          </button>
        </div>

        <F label={t("addTrade.symbol")} v={symbol} on={setSymbol} placeholder={t("addTrade.symbolPlaceholder")} />
        <div>
          <span className="label mb-2 block">{t("addTrade.assetClass")}</span>
          <Select
            value={assetClass}
            onChange={(v) => setAssetClass(v)}
            options={["Forex", "Crypto", "Commodities", "Indices", "Stocks"]}
          />
        </div>

        <F label={t("addTrade.profitLoss")} v={profit} on={setProfit} type="number" step="0.01" required placeholder={t("addTrade.profitPlaceholder")} />
        <div>
          <span className="label mb-2 block">{t("addTrade.status")}</span>
          <Select
            value={status}
            onChange={(v) => setStatus(v as "open" | "closed")}
            options={[
              { value: "closed", label: t("addTrade.closed") },
              { value: "open", label: t("addTrade.open") },
            ]}
          />
        </div>

        <F label={t("addTrade.openTime")} v={openTime} on={setOpenTime} type="datetime-local" step="1" />
        <F
          label={status === "open" ? t("addTrade.closeTimeNa") : t("addTrade.closeTime")}
          v={closeTime}
          on={setCloseTime}
          type="datetime-local"
          step="1"
          disabled={status === "open"}
        />

        <F label={t("addTrade.volume")} v={volume} on={setVolume} type="number" step="0.01" />
        <F label={t("addTrade.commission")} v={commission} on={setCommission} type="number" step="0.01" />
        <F label={t("addTrade.swap")} v={swap} on={setSwap} type="number" step="0.01" />
        <F label={t("addTrade.openPrice")} v={openPrice} on={setOpenPrice} type="number" step="0.00001" />
        <F label={t("addTrade.closePrice")} v={closePrice} on={setClosePrice} type="number" step="0.00001" />
        <F label={t("addTrade.stopLoss")} v={sl} on={setSl} type="number" step="0.00001" />
        <F label={t("addTrade.takeProfit")} v={tp} on={setTp} type="number" step="0.00001" />

        <label className="grid gap-2 sm:col-span-2">
          <span className="label">{t("addTrade.notes")}</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="input"
            placeholder={t("common.optional")}
          />
        </label>

        {error && (
          <p className="sm:col-span-2 rounded-lg bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-sm text-pnl-neg">
            {error}
          </p>
        )}

        <div className="sm:col-span-2 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost">
            {t("common.cancel")}
          </button>
          <button type="submit" disabled={m.isPending} className="btn-gold">
            {m.isPending ? t("common.saving") : t("addTrade.title")}
          </button>
        </div>
      </form>
    </section>
  );
}

function F({
  label,
  v,
  on,
  type = "text",
  step,
  placeholder,
  required,
  disabled,
}: {
  label: string;
  v: string;
  on: (v: string) => void;
  type?: string;
  step?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}) {
  return (
    <label className="grid gap-2">
      <span className="label">{label}</span>
      <input
        type={type}
        step={step}
        value={v}
        onChange={(e) => on(e.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className="input disabled:opacity-50"
      />
    </label>
  );
}
